import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, FileText, Mail, Phone, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatErr, fmtDate, typeLabel, LEAD_STATUSES } from "@/lib/api";
import { AdminTitle } from "./AdminLayout";

export const StatusPill = ({ status }) => {
  const s = LEAD_STATUSES.find((x) => x.value === status) || LEAD_STATUSES[0];
  return <span className={`inline-block text-[11px] px-2 py-1 rounded ${s.cls}`}>{s.label}</span>;
};

const LeadDialog = ({ lead, onClose, onChange, onDelete }) => {
  const nav = useNavigate();
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => { setNotes(lead?.notes || ""); }, [lead]);
  if (!lead) return null;
  const patch = async (body) => { try { onChange((await api.patch(`/admin/leads/${lead.id}`, body)).data); toast.success("Aggiornato"); } catch (e) { toast.error(formatErr(e)); } };
  const retryNotify = async () => {
    setSending(true);
    try { onChange((await api.post(`/admin/leads/${lead.id}/notify`)).data); toast.success("Notifica inviata"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setSending(false); }
  };
  const toQuote = async () => { try { const { data } = await api.post(`/admin/leads/${lead.id}/quote`); nav(`/admin/preventivi?open=${data.id}`); } catch (e) { toast.error(formatErr(e)); } };
  const rows = [["Richiesta", lead.kind === "contatto" ? "Informazioni" : "Preventivo"], ["Tipo evento", typeLabel(lead.event_type)], ["Data", lead.date_undecided ? "Indeciso/a" : fmtDate(lead.event_date)], ["Luogo", lead.location], ["Invitati", lead.guests], ["Budget", lead.budget], ["Ricevuta", fmtDate(lead.created_at)]];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="admin-root max-w-xl bg-white rounded-md" data-testid="lead-dialog">
        <DialogHeader><DialogTitle className="text-xl font-semibold">{lead.name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-4 text-sm"><a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 underline"><Mail size={14} /> {lead.email}</a>{lead.phone && <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 underline"><Phone size={14} /> {lead.phone}</a>}</div>
        <dl className="grid grid-cols-2 gap-3 text-sm">{rows.map(([k, v]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd>{v || "—"}</dd></div>)}</dl>
        {lead.message && <p className="text-sm bg-slate-50 border border-slate-200 rounded p-3 whitespace-pre-line">{lead.message}</p>}
        {!lead.email_sent && (
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm bg-amber-50 border border-amber-200 rounded p-3" data-testid="lead-email-warning">
            <span className="text-amber-900">Notifica email allo studio non inviata.</span>
            <button onClick={retryNotify} disabled={sending} className="btn-line !py-1.5 !px-3 !text-xs disabled:opacity-50" data-testid="lead-retry-notify-btn">
              <Send size={12} /> {sending ? "Invio…" : "Riprova invio"}
            </button>
          </div>
        )}
        <div className="flex flex-wrap gap-2">{LEAD_STATUSES.map((s) => <button key={s.value} onClick={() => patch({ status: s.value })} data-testid={`lead-status-${s.value}`} className={`text-xs px-3 py-1.5 border rounded ${lead.status === s.value ? "bg-slate-900 text-white border-slate-900" : "border-slate-200"}`}>{s.label}</button>)}</div>
        <textarea className="admin-input min-h-[70px]" placeholder="Note interne" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="lead-notes-input" />
        <div className="flex flex-wrap justify-between gap-2">
          <button onClick={() => onDelete(lead)} className="text-sm text-terracotta flex items-center gap-1.5" data-testid="lead-delete-btn"><Trash2 size={14} /> Elimina</button>
          <div className="flex gap-2"><button onClick={() => patch({ notes })} className="btn-line !py-2 !px-4" data-testid="lead-save-notes-btn">Salva note</button><button onClick={toQuote} className="btn-ink !py-2 !px-4" data-testid="lead-quote-btn"><FileText size={14} /> Crea preventivo</button></div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function Contacts() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const load = () => api.get("/admin/leads").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const onDelete = async (l) => { if (!window.confirm("Eliminare questa richiesta?")) return; await api.delete(`/admin/leads/${l.id}`); setOpen(null); load(); };
  return (
    <div data-testid="admin-contacts">
      <AdminTitle title="Contatti" subtitle="Richieste ricevute dal modulo del sito (preventivi e informazioni)." />
      <div className="admin-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">{["Nome", "Richiesta", "Evento", "Ricevuta", "Stato"].map((h) => <th key={h} className="p-3 font-medium">{h}</th>)}</tr></thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} onClick={() => setOpen(l)} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" data-testid={`lead-row-${l.id}`}>
                <td className="p-3"><p className="font-medium">{l.name}</p><p className="text-xs text-slate-500">{l.email}</p></td>
                <td className="p-3">{l.kind === "contatto" ? "Informazioni" : "Preventivo"}</td>
                <td className="p-3">{typeLabel(l.event_type)}{l.event_date && ` · ${fmtDate(l.event_date)}`}</td>
                <td className="p-3 text-slate-500">{fmtDate(l.created_at, { day: "numeric", month: "short" })}</td>
                <td className="p-3"><StatusPill status={l.status} /></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-slate-500" data-testid="leads-empty">Nessuna richiesta ricevuta finora.</td></tr>}
          </tbody>
        </table>
      </div>
      <LeadDialog lead={open} onClose={() => setOpen(null)} onChange={(l) => { setItems(items.map((x) => (x.id === l.id ? l : x))); setOpen(l); }} onDelete={onDelete} />
    </div>
  );
}
